import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Grid, Typography, Paper, Button, Chip, List, ListItem,
  ListItemIcon, ListItemText, CircularProgress, Alert, Skeleton,
  Stack, Divider, Container
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import API_BASE_URL from '../../Config/config';
import { useNextGenAuth } from '../context/NextGenAuthContext';

const PLAN_META = {
  starter: {
    icon: <RocketLaunchRoundedIcon />,
    color: '#06B6D4',
    gradient: 'linear-gradient(135deg, rgba(6,182,212,0.08), rgba(6,182,212,0.03))',
    borderActive: 'rgba(6,182,212,0.35)',
  },
  professional: {
    icon: <WorkspacePremiumRoundedIcon />,
    color: '#6C3BFF',
    gradient: 'linear-gradient(135deg, rgba(108,59,255,0.1), rgba(108,59,255,0.04))',
    borderActive: 'rgba(108,59,255,0.4)',
  },
  enterprise: {
    icon: <BusinessCenterRoundedIcon />,
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
    borderActive: 'rgba(245,158,11,0.35)',
  },
};

const PlanCard = ({ plan, onSelect, isSelecting }) => {
  const meta = PLAN_META[plan.id] || PLAN_META.professional;
  const { color, gradient, borderActive } = meta;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={{ y: -8 }}
      style={{ height: '100%', display: 'flex' }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: '24px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          background: plan.recommended ? '#FFFFFF' : '#FFFFFF',
          border: plan.recommended
            ? `2px solid ${color}`
            : '1px solid #E2E8F0',
          boxShadow: plan.recommended 
            ? `0 20px 40px -12px ${color}33` 
            : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: `0 25px 50px -12px ${color}44`,
            borderColor: color,
          }
        }}
      >
        {/* Recommended badge */}
        {plan.recommended && (
          <Box
            sx={{
              position: 'absolute', top: 0, right: 0,
              background: color,
              color: '#fff',
              px: 2, py: 0.8,
              borderRadius: '0 0 0 16px',
              fontSize: '0.75rem',
              fontWeight: 800,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', gap: 0.5,
              boxShadow: `0 4px 12px ${color}44`,
              zIndex: 10
            }}
          >
            <StarRoundedIcon sx={{ fontSize: 14 }} />
            {plan.badge || 'Most Popular'}
          </Box>
        )}

        {/* Icon + Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              p: 1.5, borderRadius: '16px',
              background: `${color}10`,
              color,
              display: 'flex', alignItems: 'center',
              boxShadow: `inset 0 0 0 1px ${color}20`
            }}
          >
            {React.cloneElement(meta.icon, { sx: { fontSize: 32 } })}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', fontSize: '1.25rem', lineHeight: 1.2 }}>
              {plan.name}
            </Typography>
            {plan.description && (
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                {plan.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Price */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ color: '#1E293B', fontSize: '1.5rem', fontWeight: 700 }}>{plan.currency === 'INR' ? '₹' : plan.currency}</Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, color: '#1E293B', letterSpacing: '-0.02em' }}
            >
              {plan.price.toLocaleString('en-IN')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, mt: 0.5 }}>
            +{plan.gstPercentage}% GST · One-time payment
          </Typography>
        </Box>

        <Stack spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Validity', value: `${plan.validityDays} Days` },
            { label: 'Versions', value: `${plan.versionsAllowed} Version${plan.versionsAllowed > 1 ? 's' : ''}` },
            { label: 'Referral Discount', value: `${plan.currency === 'INR' ? '₹' : ''}${plan.buyerDiscount?.toLocaleString('en-IN')}` },
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 2, borderRadius: '16px',
                background: '#F8FAFC',
                border: '1px solid #F1F5F9',
              }}
            >
              <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>
                {stat.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ mb: 4, borderStyle: 'dashed' }} />

        {/* Features */}
        <Box sx={{ flex: 1, mb: 4 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#1E293B', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Features Included
          </Typography>
          <List dense disablePadding>
            {plan.features?.map((feature, i) => (
              <ListItem key={i} disablePadding sx={{ py: 0.75 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 18, color }} />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    color: '#475569',
                    fontWeight: 500,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={isSelecting === plan.id}
          onClick={() => onSelect(plan)}
          sx={{
            borderRadius: '16px',
            py: 2,
            fontWeight: 800,
            fontSize: '1rem',
            textTransform: 'none',
            background: plan.recommended ? color : '#1E293B',
            boxShadow: plan.recommended ? `0 10px 20px -5px ${color}66` : 'none',
            '&:hover': {
              background: plan.recommended ? color : '#0F172A',
              boxShadow: plan.recommended ? `0 15px 25px -5px ${color}88` : 'none',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {isSelecting === plan.id ? (
            <CircularProgress size={24} sx={{ color: 'inherit' }} />
          ) : (
            plan.buttonText || `Get ${plan.name}`
          )}
        </Button>
      </Paper>
    </motion.div>
  );
};

const NextGenPurchase = () => {
  const { nextgen_builder } = useNextGenAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        // Explicit header as a defense-in-depth backstop alongside the
        // global axios interceptor fix — nextgenAuthMiddleware on the
        // backend 400s this route without a builder identity header.
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/plans`, {
          headers: nextgen_builder?.id != null ? { 'x-builder-id': nextgen_builder.id } : {},
        });
        // Guard against a non-array response (e.g. an error body shape)
        // ever reaching plans.map() below and crashing the page.
        setPlans(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [nextgen_builder?.id]);

  const handleSelect = (plan) => {
    navigate(`/nextgen-builder/payment/${plan.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>

        {/* ── Hero ── */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Chip
            icon={<BoltRoundedIcon sx={{ fontSize: '14px !important', color: '#6C3BFF !important' }} />}
            label="NEXTGEN BUILDER PROGRAM"
            size="small"
            sx={{
              mb: 2.5,
              bgcolor: 'rgba(108,59,255,0.08)',
              color: '#6C3BFF',
              border: '1px solid rgba(108,59,255,0.2)',
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.08em',
              height: 28,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              color: '#0F172A',
              mb: 1.5,
              fontSize: { xs: '1.8rem', md: '2.4rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            Build Real Products.{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #6C3BFF, #9B6FFF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Grow Your Skills.
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#64748B', maxWidth: 520, mx: 'auto', lineHeight: 1.7, fontWeight: 500 }}
          >
            Join The Skill Genie's NextGen Builder program. Get assigned to real projects, earn AI-powered
            code reviews, collaborate with teams, and level up your career.
          </Typography>

          {/* Referral Banner */}
          <Paper
            elevation={0}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 3,
              px: 2.5,
              py: 1.25,
              borderRadius: '12px',
              background: 'linear-gradient(90deg, rgba(34,197,94,0.07), rgba(34,197,94,0.03))',
              border: '1px solid rgba(34,197,94,0.2)',
            }}
          >
            <LocalOfferRoundedIcon sx={{ color: '#22C55E', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#22C55E', fontWeight: 700, fontSize: '0.85rem' }}>
              Have a referral code? Save up to ₹2,500 at checkout!
            </Typography>
          </Paper>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Plan Cards ── */}
        {loading ? (
          <Grid container spacing={3} alignItems="stretch">
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={520} sx={{ borderRadius: '20px' }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3} alignItems="stretch">
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id} sx={{ display: 'flex' }}>
                <PlanCard plan={plan} onSelect={handleSelect} isSelecting={selecting} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Trust Indicators ── */}
        <Box
          sx={{
            mt: 5, pt: 3.5,
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, md: 4 },
            flexWrap: 'wrap',
          }}
        >
          {[
            { icon: <SecurityRoundedIcon />, label: '256-bit SSL Encrypted' },
            { icon: <VerifiedUserRoundedIcon />, label: 'Razorpay Secured' },
            { icon: <CheckCircleRoundedIcon />, label: 'One-time Payment' },
          ].map(({ icon, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {React.cloneElement(icon, { sx: { fontSize: 16, color: '#94A3B8' } })}
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

export default NextGenPurchase;
